import {ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, ViewChild} from '@angular/core';
import {User, UserStatus} from "../interfaces/user";
import {SubscribedComponent} from "../subscribed.component";
import {AuthService} from "../shared/auth.service";
import {NbDateService, NbDialogService, NbMenuItem, NbMenuService, NbPopoverDirective, NbSidebarService} from "@nebular/theme";
import {Globals} from "../shared/Globals";
import {BehaviorSubject, Subscription} from "rxjs";
import {PopupDialogComponent} from "../dialogs/popup-dialog/popup-dialog.component";
import {CurClientSelectorComponent} from "../controls/cur-client-selector/cur-client-selector.component";
import {Router} from "@angular/router";
import {TitleService} from "../shared/title.service";
import {TranslateService} from "@ngx-translate/core";
import {SettingsService} from "../shared/settings.service";

@Component({
    selector: 'bst-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent extends SubscribedComponent{

  public title: string = Globals.APP_NAME;
  public user: User;
  public readonly searching = new BehaviorSubject<boolean>(false);
  public readonly Globals = Globals;
  public usermenu: NbMenuItem[];
  public mobilemenu: NbMenuItem[] = [];
  private dsub: Subscription;
  public accountMenuOpen = false;

  @ViewChild('accountControl') accountControl: ElementRef<HTMLElement>;
  @ViewChild('accountTrigger') accountTrigger: ElementRef<HTMLButtonElement>;

  @HostListener('document:click', ['$event'])
  closeAccountMenuOutside(event: MouseEvent): void {
    if (!this.accountControl?.nativeElement.contains(event.target as Node)) {
      this.accountMenuOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  closeAccountMenu(): void {
    if (this.accountMenuOpen) {
      this.accountMenuOpen = false;
      this.accountTrigger?.nativeElement.focus();
    }
  }

  @HostListener('document:focusin', ['$event'])
  closeAccountMenuOnFocusLeave(event: FocusEvent): void {
    if (!this.accountControl?.nativeElement.contains(event.target as Node)) {
      this.accountMenuOpen = false;
    }
  }

  @ViewChild('mobilePop', { read: NbPopoverDirective }) mobilePop: NbPopoverDirective;

  /** Close the mobile-menu popover (template refs don't cross the <ng-template>). */
  public hideMobileMenu(): void {
    this.mobilePop?.hide();
  }

  constructor(public auth: AuthService,
              private dialog: NbDialogService,
              private router: Router,
              public titleService: TitleService,
              public translate: TranslateService,
              private menu: NbMenuService,
              private sidebar: NbSidebarService,
              private ngZone: NgZone,
              private dateService: NbDateService<unknown>,
              private settings: SettingsService,
              private detector: ChangeDetectorRef) {
    super();
  }

  public readonly languages = Globals.LANGUAGES;
  // Each language named in itself (English, Nederlands), so it is findable whatever the UI shows.
  private readonly languageItem: NbMenuItem = {
    title: 'Language',
    icon: 'globe-outline',
    children: this.languages.map(lang => ({title: new Intl.DisplayNames([lang], {type: 'language'}).of(lang), data: () => this.setLanguage(lang)})),
  };

  public setLanguage(lang: string) {
    // use(), not a currentLang assignment: only use() loads the translations and
    // fires onLangChange, so the translate pipe actually re-renders.
    this.translate.use(lang);
    // Keep the datepicker's day/month parsing in step with the chosen UI
    // language, see app.component.ts's applyLanguage() for the initial sync (#1175).
    this.dateService.setLocale(lang);
    if (this.user) {
      this.settings.putUserSetting('language', lang);
      void this.settings.saveUserSettings(true);
    } else {
      try { localStorage.setItem(Globals.GUEST_LANGUAGE_KEY, lang); } catch {}
    }
  }

  private markLanguage() {
    this.languageItem.children.forEach((c, i) => c.icon = this.languages[i] === this.translate.currentLang ? 'checkmark-outline' : undefined);
  }

  // Hide the tenant logo slot if its file 404s (e.g. GCS assets unreachable locally)
  public logoBroken = false;

  get logo(): string{
    if(Globals.domainInfo && Globals.domainInfo.logo){
      return Globals.domainInfo.logo
    }
    else if(this.user && this.user.client && this.user.client.logo && this.user.client.logo.preview){
      return Globals.storage(this.user.client.logo.preview);
    }
    return null;
  }

  toggleSidebar(): boolean {
    this.sidebar.toggle(false, 'menu');
    return false;
  }

  async ngOnInit() {
    // Bootstrap already resolved this value. Render the signed-in branch on the
    // first check rather than switching it after the translation promise settles.
    this.user = this.auth.user.getValue();
    // usermenu is an inline <nb-menu> (no CDK overlay teardown race), so it keeps
    // the onItemClick handler for its one data-callback item (cur-client switcher).
    // mobilemenu moved to an NbPopover (see template) — it no longer routes here.
    this.subs.push(this.menu.onItemClick().subscribe((o) => {
      if (o.tag === 'usermenu') {
        if (o.item.data && typeof o.item.data === 'function') {
          this.ngZone.run(() => o.item.data.call(this));
        }
        if (!o.item.children?.length) { this.accountMenuOpen = false; }
      }
    }));

    // this.subs.push(this.router.events.subscribe((item: RouterEvent) => {
    //   if(item instanceof ActivationEnd){
    //     const end = <ActivationEnd>item;
    //     const comp = <any>(end.snapshot.component);
    //     console.log(end);
    //     if(comp && typeof comp.header === 'string'){
    //       this.subtitle = comp.header;
    //     }
    //     else{
    //       this.subtitle = null;
    //     }
    //   }
    // }));

    this.markLanguage();
    this.subs.push(this.translate.onLangChange.subscribe(() => this.markLanguage()));

    const yclients = await this.translate.get("yourclients").toPromise();
    this.subs.push(this.auth.user.subscribe( (user: User) => {
      this.user = user;
      this.accountMenuOpen = false;
      if(this.user) {
        this.usermenu = [
          {
            title: 'Account settings',
            icon: 'settings-2-outline',
            link: 'settings'
          },
          {
            title: this.user.client?.name || yclients,
            icon: 'briefcase-outline',
            data: () => {
              if (this.dsub) {
                this.dsub.unsubscribe();
                this.dsub = null;
              }

              this.dsub = this.dialog.open<PopupDialogComponent<CurClientSelectorComponent>>(PopupDialogComponent, {
                context: {
                  comp: CurClientSelectorComponent,
                  componentContext: {
                    user: this.user
                  }
                }
              }).onClose.subscribe(val => {

              });
            }
          }
        ];

        // Console link is only useful on the admin subdomain. The route
        // is registered ONLY on flix.{env}.teamertime.com via
        // Route::domain(config('platform.admin_host')), so clicking it from
        // a tenant subdomain would 404. Hide on tenant subdomains so it's
        // not a dead UI affordance. The host-startsWith heuristic covers
        // flix.localhost (dev), flix.staging.teamertime.com (staging), and
        // flix.teamertime.com (prod). URL stays /platform/overview so
        // existing bookmarks survive the rename.
        if (this.user.is_super_user && window.location.hostname.startsWith('flix.')) {
          this.usermenu.push({
            title: 'Console',
            icon: 'globe-2-outline',
            link: '/platform/overview'
          });
        }

        // if(this.user.status !== UserStatus.NEW){
        //   this.usermenu.unshift({
        //     title: 'My profile',
        //     icon: 'person-outline',
        //     link: '/users/me',
        //     pathMatch: 'full'
        //   });
        // }

        // if(this.auth.isStaffer || this.auth.isAdmin){
        //   this.usermenu.push({
        //     title: 'Archive bin',
        //     icon: 'trash-2-outline',
        //     link: 'bin'
        //   });
        // }

        this.usermenu.push(this.languageItem);

        this.usermenu.push({
          title: 'Help',
          icon: 'question-mark-circle-outline',
          url: 'mailto:support@teamertime.com',
          target: '_blank',
        });

        this.usermenu.push({
          title: 'Logout',
          icon: 'unlock-outline',
          link: 'logout'
        });

        this.mobilemenu = [
          {
            title: 'Help',
            icon: 'question-mark-circle-outline',
            url: 'mailto:support@teamertime.com',
            target: '_blank',
          },
        ];
      }
      else{
        this.usermenu = [];
      }
      this.detector.markForCheck();
    }));
  }

  userlogo(user: User) {
    return user.logo && user.logo.preview ? Globals.storage(user.logo.preview) : user.provider_avatar;
  }

}
